import React, { useEffect, useState } from "react"
import { Table, Skeleton, Button, Tooltip } from "antd"
import { DownloadOutlined, PlusOutlined, EditOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons"
import { Link, useRouteMatch } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { downloadDocument, fetchDocuments, filterDocuments, selectDocuments, selectLoading, selectPagination, selectTotal } from "../documentsSlice"
import ColumnSearchFilter from "../../../components/utilityComponents/table/ColumnSearchFilter"
import ColumnDateFilter from "../../../components/utilityComponents/table/ColumnDateFilter"
import ColumnHeaderTitle from "../../../components/utilityComponents/table/ColumnHeaderTitle"
import { allObjectValuesEmpty } from "../../../functions/objectFunctions/allObjectValuesEmpty"
import { tableFiltersApplied } from "../../../functions/objectFunctions/tableFiltersApplied"
import moment from "moment"

/// matches the API's own default ordering, so the plain fetchDocuments path and the
/// q[s]=updated_at desc path return the same rows in the same order
const DEFAULT_SORT = { column: "updated_at", direction: "desc" }

export default function DocumentsTable({ type, id, display }) {
    const dispatch = useDispatch()

    const documents = useSelector(selectDocuments)
    const loading = useSelector(selectLoading)
    const total = useSelector(selectTotal)
    const pagination = useSelector(selectPagination)

    const { url } = useRouteMatch()

    const [loaded, setLoaded] = useState(false)
    const [filters, setFilters] = useState({})
    const [sort, setSort] = useState(DEFAULT_SORT)

    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            dispatch(fetchDocuments(pagination, type, id))
        }
    }, [dispatch, id, pagination, type, loaded])

    const isDefaultSort = sort.column === DEFAULT_SORT.column && sort.direction === DEFAULT_SORT.direction

    const handleFetchInitialDocuments = () => {
        dispatch(fetchDocuments({ ...pagination, current: 1 }, type, id))
    }

    const handleFilterDocuments = (newPagination = null, newSort = null) => {
        return dispatch(
            filterDocuments(
                {
                    filters: filters,
                    pagination: !!newPagination ? newPagination : pagination,
                    sort: !!newSort ? newSort : sort,
                },
                type,
                id
            )
        )
    }

    const handleSortDocumentsChange = (column) => () => {
        /// toggle off this column's own direction, not whatever the previously sorted column was
        /// left on -- with three sortable columns that difference is very visible
        let newDirection = sort.column === column && sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        setSort(newSort)
        handleFilterDocuments({ ...pagination, current: 1 }, newSort)
    }

    const handleTableChange = (newPagination) => {
        if (allObjectValuesEmpty(filters) && isDefaultSort) {
            dispatch(fetchDocuments(newPagination, type, id))
        } else {
            handleFilterDocuments(newPagination)
        }
    }

    const handleResetFilters = (event) => {
        /// href="#" would otherwise jump the page to the top and append a hash to the url
        event.preventDefault()
        setFilters({})
        setSort(DEFAULT_SORT)
        handleFetchInitialDocuments()
    }

    const download = (record) => {
        dispatch(downloadDocument(record.id))
    }

    /// ColumnSearchFilter/ColumnDateFilter hardcode which scope ids they forward and know nothing
    /// about type/id -- but both only ever call dispatch(props.filterAction(query, scopes)), so
    /// currying the scope in here keeps the shared components untouched.
    const documentsFilterAction = (query) => filterDocuments(query, type, id)

    /// title is the sort key (it also drives which arrow is highlighted), altTitle is the label
    const renderColumnHeaderTitle = (title, altTitle) => {
        return <ColumnHeaderTitle title={title} altTitle={altTitle} handleSortChange={handleSortDocumentsChange} sort={sort} />
    }

    /// takes a boolean rather than a key, since a date column has to OR its _gteq and _lteq keys
    /// and tableFiltersApplied only checks one at a time
    const renderSearchOutlinedButton = (applied) => {
        return <SearchOutlined style={{ fontSize: "16px", color: applied ? "#1890ff" : undefined }} />
    }

    const renderResetFilters = () => {
        /// ColumnDateFilter has no clear button of its own, so this link is the only way to clear
        /// a date range -- it has to appear for a non-default sort too, not just for filters
        if (allObjectValuesEmpty(filters) && isDefaultSort) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <a href="#" onClick={handleResetFilters}>
                    <FilterOutlined />
                    Reset Filters
                </a>
            </div>
        )
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>{display || type} Documents</h2>
                            <div className="badge">{total}</div>
                        </div>
                        <div className="actions">
                            <div className="table-action-links">
                                {renderResetFilters()}

                                {/* @TODO how do we get the type & id into here if it's not nested... */}
                                <Link to={`${url}/new`}>
                                    <PlusOutlined />
                                    Add New
                                </Link>
                            </div>
                        </div>
                    </div>
                    <Skeleton loading={loading}>
                        <Table dataSource={documents} onChange={handleTableChange} rowKey={(r) => r.id} pagination={{ ...pagination, showSizeChanger: true, total: total }}>
                            <Table.Column
                                title={renderColumnHeaderTitle("title", "Title")}
                                key="title"
                                dataIndex="title"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialDocuments}
                                        filterAction={documentsFilterAction}
                                        filterType={"title_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        visible={visible}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton(tableFiltersApplied("title_cont", filters))}
                            />
                            <Table.Column
                                // SORT uses the raw created_at column -- more precise than the truncated date,
                                // and no intra-day ties. FILTER uses the created_at_date ransacker. The raw
                                // created_at_gteq/_lteq keys are NOT filterable on the API: they are silently
                                // ignored and return unfiltered results. Do not "fix" this mismatch.
                                title={renderColumnHeaderTitle("created_at", "Created At")}
                                key="created_at"
                                dataIndex="created_at"
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialDocuments}
                                        filterAction={documentsFilterAction}
                                        filterType={"created_at_date"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        visible={visible}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton(tableFiltersApplied("created_at_date_gteq", filters) || tableFiltersApplied("created_at_date_lteq", filters))}
                                render={(text, record) => moment(record.created_at).format("MM/DD/YYYY")}
                            />
                            <Table.Column
                                // see the created_at column above -- sort on updated_at, filter on updated_at_date
                                title={renderColumnHeaderTitle("updated_at", "Updated At")}
                                key="updated_at"
                                dataIndex="updated_at"
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialDocuments}
                                        filterAction={documentsFilterAction}
                                        filterType={"updated_at_date"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        visible={visible}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton(tableFiltersApplied("updated_at_date_gteq", filters) || tableFiltersApplied("updated_at_date_lteq", filters))}
                                render={(text, record) => moment(record.updated_at).format("MM/DD/YYYY")}
                            />
                            <Table.Column
                                title="Download"
                                key="download"
                                render={(text, record) => (
                                    <Button type="primary" onClick={() => download(record)}>
                                        <DownloadOutlined />
                                    </Button>
                                )}
                            />
                            <Table.Column
                                title="Edit"
                                key="edit"
                                render={(text, record) => (
                                    <div className="row-actions" style={{ display: "inline-block" }}>
                                        <Link to={`${url}/${record.id}/edit`}>
                                            <Tooltip title="Edit">
                                                <EditOutlined />
                                            </Tooltip>
                                        </Link>
                                    </div>
                                )}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
        </div>
    )
}
